import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { supabase } from "../config";
import KathakalaPlayer from "../components/KathakalaPlayer";
import {
  CATEGORY_LABELS,
  URL_TO_DB_CATEGORY,
  DB_TO_URL_CATEGORY,
  SLUG_TO_PERFORMANCE_CATEGORY_TELUGU,
  normalizeCategory,
  RANK_LABELS,
  SPECIAL_JURY_LABEL,
  isWriterVoiceCategory,
  extractYouTubeId,
} from "../utils/jeedigunta";
import { FaTrophy, FaArrowLeft, FaPlay, FaStar, FaMedal, FaUserCircle } from "react-icons/fa";

const PLACEHOLDER_AVATAR =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%236B1D2A'/><text x='50' y='62' text-anchor='middle' font-size='52' fill='%23D4A843'>🏆</text></svg>`
  );

const seedJeevitam = "అందమైన";

// Pedestal heights (1st > 2nd > 3rd) for the podium look.
const PEDESTAL_HEIGHTS = { 1: "h-44 md:h-56", 2: "h-32 md:h-44", 3: "h-24 md:h-36" };
const PEDESTAL_ORDER = [2, 1, 3]; // visual: 2nd, 1st (centered), 3rd

const JeedigntaCategory = () => {
  const { seasonSlug, categorySlug } = useParams();
  const [season, setSeason] = useState(null);
  const [results, setResults] = useState([]);
  const [storytellersById, setStorytellersById] = useState({});
  const [storiesById, setStoriesById] = useState({});
  const [writersById, setWritersById] = useState({});
  const [perfsById, setPerfsById] = useState({});
  const [shortlist, setShortlist] = useState([]);
  const [reels, setReels] = useState([]);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeMeta, setActiveMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const playerRef = useRef(null);

  const dbCategory = URL_TO_DB_CATEGORY[categorySlug];

  useEffect(() => {
    const catLabel = CATEGORY_LABELS[dbCategory];
    const cat = catLabel ? catLabel.en : "";
    document.title = cat
      ? `${cat} · Jeedigunta Kathakala Puraskaralu ${seasonSlug || ""}`.trim()
      : `Jeedigunta Kathakala Puraskaralu ${seasonSlug || ""}`.trim();
  }, [dbCategory, seasonSlug]);

  useEffect(() => {
    if (!dbCategory) {
      setNotFound(true);
      return;
    }
    (async () => {
      setLoading(true);
      const { data: s } = await supabase
        .from("jeedigunta_seasons")
        .select("season_id, season_name, season_year, slug, status")
        .eq("slug", seasonSlug)
        .maybeSingle();
      if (!s) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setSeason(s);

      const { data: r } = await supabase
        .from("jeedigunta_results")
        .select(
          "result_id, category, rank, is_special_jury, joint_group, performance_id, storyteller_name_te, story_name_te, writer_name_te, notes"
        )
        .eq("season_id", s.season_id)
        .eq("category", dbCategory)
        .order("rank", { ascending: true, nullsFirst: false })
        .order("is_special_jury", { ascending: true });
      setResults(r || []);

      // Collect performance ids from results, then fetch performances for join data.
      const perfIds = (r || []).filter((x) => x.performance_id).map((x) => x.performance_id);
      let perfRows = [];
      if (perfIds.length) {
        const { data: pf } = await supabase
          .from("performances")
          .select(
            "performance_id, storyteller_id, story_id, episode_id, story_start_sec, story_end_sec, story_name_telugu, storyteller_name_telugu"
          )
          .in("performance_id", perfIds);
        perfRows = pf || [];
      }

      // Also pull the full shortlist for this (season, dbCategory) → nominees table.
      const teluguCat = SLUG_TO_PERFORMANCE_CATEGORY_TELUGU[dbCategory];
      const teluguCatList = Array.isArray(teluguCat) ? teluguCat : [teluguCat];
      const { data: shortRows } = await supabase
        .from("jeedigunta_shortlist")
        .select("performance_id")
        .eq("season_id", s.season_id);

      let shortlistPerfIds = (shortRows || []).map((x) => x.performance_id);

      let shortlistFullPerf = [];
      if (shortlistPerfIds.length) {
        const { data: pfAll } = await supabase
          .from("performances")
          .select(
            "performance_id, storyteller_id, story_id, episode_id, story_start_sec, story_end_sec, story_name_telugu, storyteller_name_telugu, category"
          )
          .in("performance_id", shortlistPerfIds);
        shortlistFullPerf = (pfAll || []).filter((p) =>
          teluguCatList.includes(normalizeCategory(p.category))
        );
      }

      // Combine winners' perf rows with shortlist perf rows (dedup).
      const allPerfMap = {};
      [...perfRows, ...shortlistFullPerf].forEach((p) => {
        allPerfMap[p.performance_id] = p;
      });
      setPerfsById(allPerfMap);

      // Fetch storytellers + stories + writers used by these perfs.
      const stIds = Array.from(
        new Set(Object.values(allPerfMap).map((p) => p.storyteller_id).filter(Boolean))
      );
      const stoIds = Array.from(
        new Set(Object.values(allPerfMap).map((p) => p.story_id).filter(Boolean))
      );
      const epIds = Array.from(
        new Set(Object.values(allPerfMap).map((p) => p.episode_id).filter(Boolean))
      );

      const [
        { data: stRows },
        { data: stoRows },
        { data: epRows },
      ] = await Promise.all([
        stIds.length
          ? supabase
              .from("storytellers")
              .select("storyteller_id, storyteller_telugu, storyteller_english, photo_url")
              .in("storyteller_id", stIds)
          : Promise.resolve({ data: [] }),
        stoIds.length
          ? supabase
              .from("stories")
              .select("story_id, title_telugu, writer_id")
              .in("story_id", stoIds)
          : Promise.resolve({ data: [] }),
        epIds.length
          ? supabase
              .from("episodes")
              .select("episode_id, video_id, youtube_broadcast_id, scheduled_start, title")
              .in("episode_id", epIds)
          : Promise.resolve({ data: [] }),
      ]);

      const stMap = {};
      (stRows || []).forEach((x) => (stMap[x.storyteller_id] = x));
      setStorytellersById(stMap);

      const writerIds = Array.from(
        new Set((stoRows || []).map((x) => x.writer_id).filter(Boolean))
      );
      let wrMap = {};
      if (writerIds.length) {
        const { data: wrRows } = await supabase
          .from("writers")
          .select("writer_id, writer_telugu, writer_english")
          .in("writer_id", writerIds);
        (wrRows || []).forEach((w) => (wrMap[w.writer_id] = w));
      }
      setWritersById(wrMap);

      const stoMap = {};
      (stoRows || []).forEach((x) => (stoMap[x.story_id] = x));
      setStoriesById(stoMap);

      const epMap = {};
      (epRows || []).forEach((x) => (epMap[x.episode_id] = x));

      // Build shortlist display rows
      const sl = (shortlistFullPerf || []).map((p) => ({
        performance_id: p.performance_id,
        storyteller_id: p.storyteller_id,
        story_id: p.story_id,
        episode: epMap[p.episode_id],
        start_sec: p.story_start_sec || 0,
        story_name_telugu: p.story_name_telugu,
        storyteller_name_telugu: p.storyteller_name_telugu,
      }));
      setShortlist(sl);

      // Highlight reels for this (season, category) — also include reels with NULL category (whole season).
      const { data: reelRows } = await supabase
        .from("jeedigunta_highlight_reels")
        .select("reel_id, season_id, category, performance_id, youtube_url, youtube_video_id, title, description, thumbnail_url, sort_order")
        .eq("season_id", s.season_id)
        .order("sort_order", { ascending: true });
      const reelsForCat = (reelRows || []).filter(
        (r) => r.category === null || r.category === dbCategory
      );
      setReels(reelsForCat);

      setLoading(false);
    })();
  }, [seasonSlug, categorySlug, dbCategory]);

  if (notFound) return <Navigate to={`/jeedigunta/${seasonSlug || ""}`} replace />;

  const catLabel = CATEGORY_LABELS[dbCategory] || { te: dbCategory, en: dbCategory };
  const isWriterCat = isWriterVoiceCategory(dbCategory);

  // ── Build display data for podium + special jury ──
  const podiumByRank = { 1: [], 2: [], 3: [] };
  const specialJury = [];
  results.forEach((r) => {
    if (r.is_special_jury) specialJury.push(r);
    else if (r.rank in podiumByRank) podiumByRank[r.rank].push(r);
  });

  // Compute a stable "storyteller key" for grouping (handles both DB-linked
  // performances and rows that only carry a typed Telugu name).
  const storytellerKey = (r) => {
    const perf = r.performance_id ? perfsById[r.performance_id] : null;
    return (
      perf?.storyteller_id ||
      r.storyteller_name_te ||
      `__row_${r.result_id}`
    );
  };

  // Group rows in a single rank/jury bucket by storyteller. If the same
  // person has multiple results in the same bucket (e.g., one Special
  // Jury award covering two stories), we render ONE card with the
  // stories listed together.
  const groupByStoryteller = (rows) => {
    const byKey = new Map();
    const order = [];
    rows.forEach((r) => {
      const k = storytellerKey(r);
      if (!byKey.has(k)) {
        byKey.set(k, { primary: r, rows: [r] });
        order.push(k);
      } else {
        byKey.get(k).rows.push(r);
      }
    });
    return order.map((k) => byKey.get(k));
  };

  // Build special-jury groups. First cluster by joint_group (multiple
  // people sharing one award) OR by storyteller (one person multiple
  // stories); then within each cluster sub-group by storyteller.
  const specialJuryGroups = (() => {
    const out = [];
    const claimed = new Set();
    specialJury.forEach((r) => {
      if (claimed.has(r.result_id)) return;
      let cluster;
      if (r.joint_group != null) {
        cluster = specialJury.filter((x) => x.joint_group === r.joint_group);
      } else {
        const myKey = storytellerKey(r);
        cluster = specialJury.filter(
          (x) => x.joint_group == null && storytellerKey(x) === myKey
        );
        if (cluster.length === 0) cluster = [r];
      }
      cluster.forEach((x) => claimed.add(x.result_id));
      out.push(groupByStoryteller(cluster));
    });
    return out;
  })();

  // Format a list of story titles for one storyteller — single name when
  // there's one, "రెండు కథలు : a & b" style when there's more than one.
  const TELUGU_NUMS = { 2: "రెండు", 3: "మూడు", 4: "నాలుగు", 5: "ఐదు" };
  const formatStoriesLine = (rows) => {
    const titles = rows
      .map((r) => {
        const perf = r.performance_id ? perfsById[r.performance_id] : null;
        const sto = perf ? storiesById[perf.story_id] : null;
        return (
          sto?.title_telugu ||
          perf?.story_name_telugu ||
          r.story_name_te ||
          ""
        );
      })
      .filter((s) => s);
    if (titles.length <= 1) return titles[0] || "—";
    const numLabel = TELUGU_NUMS[titles.length] || titles.length;
    return `${numLabel} కథలు : ${titles.join(" & ")}`;
  };

  // Helper: turn a result row into displayable bits.
  const resolveResult = (r) => {
    const perf = r.performance_id ? perfsById[r.performance_id] : null;
    const st = perf ? storytellersById[perf.storyteller_id] : null;
    const sto = perf ? storiesById[perf.story_id] : null;
    const writer = sto?.writer_id ? writersById[sto.writer_id] : null;
    const storytellerTe =
      st?.storyteller_telugu ||
      perf?.storyteller_name_telugu ||
      r.storyteller_name_te ||
      "—";
    const storyTe =
      sto?.title_telugu || perf?.story_name_telugu || r.story_name_te || "—";
    const writerTe = isWriterCat
      ? "స్వీయ రచన"
      : writer?.writer_telugu || r.writer_name_te || null;
    const photoUrl = st?.photo_url || PLACEHOLDER_AVATAR;
    return { perf, st, sto, writer, storytellerTe, storyTe, writerTe, photoUrl };
  };

  const handlePlay = (perf, storytellerTe, storyTe) => {
    if (!perf) return;
    // Find video id from joined episodes via shortlist or perfsById episode_id.
    // We need episodes.video_id — fetch lazily from perfsById join we already did.
    // shortlist rows carry episode but result-perfsById does not. Use fallback.
    // For winners we don't have the episode row in scope here; do a quick fetch.
    const epId = perf.episode_id;
    if (!epId) return;
    (async () => {
      const { data: ep } = await supabase
        .from("episodes")
        .select("video_id, youtube_broadcast_id, title")
        .eq("episode_id", epId)
        .maybeSingle();
      const vid = ep?.video_id || ep?.youtube_broadcast_id;
      if (!vid) return;
      setActiveVideo({ videoId: vid, startSec: perf.story_start_sec || 0 });
      setActiveMeta({
        title: `${storytellerTe} — ${storyTe}`,
        meta: ep?.title || "",
        url: `https://www.youtube.com/watch?v=${vid}${perf.story_start_sec ? `&t=${perf.story_start_sec}` : ""}`,
      });
    })();
  };

  const playReel = (reel) => {
    const vid = reel.youtube_video_id || extractYouTubeId(reel.youtube_url);
    if (!vid) return;
    setActiveVideo({ videoId: vid, startSec: 0 });
    setActiveMeta({
      title: reel.title || "Highlight Reel",
      meta: reel.description || "",
      url: reel.youtube_url,
    });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-kk-ink via-kk-maroon-deep to-kk-ink text-kk-cream">
      <div className="max-w-7xl mx-auto px-3 md:px-5 pt-4 pb-8 md:pt-6 md:pb-10">
        {/* Breadcrumb */}
        <div className="text-sm text-kk-gold/70 mb-4">
          <Link to="/jeedigunta" className="hover:text-kk-gold">
            పురస్కారాలు
          </Link>{" "}
          ›{" "}
          <Link to={`/jeedigunta/${seasonSlug}`} className="hover:text-kk-gold">
            {seasonSlug}
          </Link>{" "}
          › <span className="text-kk-cream">{catLabel.te}</span>
        </div>

        {/* Sticky-ish Player at top when active (full width above the grid) */}
        {activeVideo && (
          <div className="mb-6 rounded-xl overflow-hidden border border-kk-gold/20 shadow-2xl">
            <KathakalaPlayer
              ref={playerRef}
              videoId={activeVideo.videoId}
              startSec={activeVideo.startSec}
              nowPlayingTitle={activeMeta?.title}
              nowPlayingMeta={activeMeta?.meta}
              youtubeUrl={activeMeta?.url}
            />
          </div>
        )}

        {/* Side-by-side: Jeedigunta hero logo on the left, all category
            content (header, podium, special jury, reels, nominees) on the
            right. Stacks vertically on mobile. */}
        <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
          {/* ── LEFT: Hero logo (sticky on desktop) ── */}
          <div className="flex justify-center md:justify-start md:sticky md:top-6">
            <img
              src="/logos/jeedigunta_puraskaralu_logo.jpg"
              alt="జీడిగుంట కథాకళ పురస్కారాలు"
              className="w-36 md:w-48 lg:w-56 h-auto rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] border border-kk-gold/30"
            />
          </div>

          {/* ── RIGHT: header + podium + special jury + reels + nominees ── */}
          <div className="min-w-0">
        {/* Header */}
        <header className="mb-6 md:mb-8">
          <div className="inline-flex items-center gap-3 mb-1">
            <FaTrophy className="text-kk-gold text-2xl md:text-3xl" />
            <h1 className="text-2xl md:text-4xl font-bold text-kk-gold-light">
              {catLabel.te}
            </h1>
          </div>
          <div className="text-base md:text-lg text-kk-cream/75 italic font-serif">
            {catLabel.en} · {seasonSlug}
          </div>
        </header>

        {/* Podium */}
        {loading ? (
          <p className="text-kk-cream/60">Loading…</p>
        ) : (
          <>
            <section className="mb-10">
              <h2 className="text-lg md:text-xl font-semibold text-kk-gold mb-5 text-center">
                విజేతలు · Winners Podium
              </h2>
              <div className="grid grid-cols-3 gap-2 md:gap-4 items-end">
                {PEDESTAL_ORDER.map((rank) => {
                  const rs = podiumByRank[rank] || [];
                  if (rs.length === 0) {
                    return (
                      <div key={rank} className="flex flex-col items-center">
                        <div className="text-kk-cream/30 text-sm mb-2">
                          {RANK_LABELS[rank]?.te}
                        </div>
                        <div
                          className={`w-full ${PEDESTAL_HEIGHTS[rank]} bg-kk-ink/40 border border-kk-gold/10 rounded-t-lg flex items-center justify-center text-kk-gold/30`}
                        >
                          —
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={rank} className="flex flex-col items-center">
                      {/* Winner card stack (handles joint awards AND
                          multi-story-per-storyteller by stacking) */}
                      <div className="flex flex-col items-center gap-2 mb-3 w-full">
                        {groupByStoryteller(rs).map(({ primary, rows }) => {
                          const d = resolveResult(primary);
                          const storyLine = formatStoriesLine(rows);
                          return (
                            <button
                              key={primary.result_id}
                              type="button"
                              onClick={() => handlePlay(d.perf, d.storytellerTe, d.storyTe)}
                              disabled={!d.perf}
                              className="group w-full flex flex-col items-center text-center disabled:cursor-default"
                            >
                              <div
                                className={`relative ${
                                  rank === 1 ? "w-20 h-20 md:w-28 md:h-28" : "w-16 h-16 md:w-20 md:h-20"
                                } rounded-full overflow-hidden border-4 ${
                                  rank === 1
                                    ? "border-kk-gold shadow-[0_0_20px_rgba(212,168,67,0.4)]"
                                    : rank === 2
                                    ? "border-kk-gold-light/80"
                                    : "border-amber-700"
                                }`}
                              >
                                <img
                                  src={d.photoUrl}
                                  alt={d.storytellerTe}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = PLACEHOLDER_AVATAR;
                                  }}
                                />
                              </div>
                              <div
                                className={`mt-2 text-[11px] md:text-sm font-semibold leading-tight ${
                                  rank === 1 ? "text-kk-gold-light" : "text-kk-cream"
                                }`}
                              >
                                {d.storytellerTe}
                              </div>
                              <div className="text-[10px] md:text-xs text-kk-cream/70 italic mt-0.5">
                                {storyLine}
                              </div>
                              {d.writerTe && (
                                <div className="text-[10px] md:text-[11px] text-kk-gold/60 mt-0.5">
                                  రచన: {d.writerTe}
                                </div>
                              )}
                              {d.perf && (
                                <span className="mt-1 inline-flex items-center gap-1 text-[10px] md:text-xs text-kk-gold opacity-0 group-hover:opacity-100 transition-opacity">
                                  <FaPlay className="text-[8px]" /> Play
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {/* Pedestal */}
                      <div
                        className={`w-full ${PEDESTAL_HEIGHTS[rank]} flex items-start justify-center pt-2 rounded-t-lg border-x-2 border-t-2 ${
                          rank === 1
                            ? "bg-gradient-to-b from-kk-gold/30 to-kk-maroon/40 border-kk-gold"
                            : rank === 2
                            ? "bg-gradient-to-b from-kk-gold-light/20 to-kk-maroon/30 border-kk-gold-light/60"
                            : "bg-gradient-to-b from-amber-700/20 to-kk-maroon/30 border-amber-700/60"
                        }`}
                      >
                        <div
                          className={`flex flex-col items-center ${
                            rank === 1 ? "text-kk-gold" : "text-kk-cream/80"
                          }`}
                        >
                          <FaMedal className="text-2xl md:text-3xl mb-1" />
                          <div className="text-base md:text-2xl font-bold">{rank}</div>
                          <div className="text-[10px] md:text-xs leading-tight px-1">
                            {RANK_LABELS[rank]?.te}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Special Jury */}
            {specialJury.length > 0 && (
              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-semibold text-kk-gold mb-4 text-center flex items-center justify-center gap-2">
                  <FaStar className="text-kk-gold-light" /> {SPECIAL_JURY_LABEL.te}{" "}
                  <span className="text-kk-cream/60 italic font-serif text-base">
                    · {SPECIAL_JURY_LABEL.en}
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {specialJuryGroups.map((group, idx) => (
                    <div
                      key={idx}
                      className="p-4 bg-kk-ink/60 border border-kk-gold-light/40 rounded-xl"
                    >
                      {group.map(({ primary, rows }, i) => {
                        const d = resolveResult(primary);
                        const storyLine = formatStoriesLine(rows);
                        return (
                          <div key={primary.result_id}>
                            {i > 0 && (
                              <div className="border-t border-dashed border-kk-gold/30 my-3" />
                            )}
                            <button
                              type="button"
                              onClick={() => handlePlay(d.perf, d.storytellerTe, d.storyTe)}
                              disabled={!d.perf}
                              className="w-full flex items-center gap-3 text-left disabled:cursor-default"
                            >
                              <img
                                src={d.photoUrl}
                                alt={d.storytellerTe}
                                className="w-14 h-14 rounded-full object-cover border-2 border-kk-gold-light/60"
                                onError={(e) => {
                                  e.target.src = PLACEHOLDER_AVATAR;
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-kk-cream font-semibold truncate">
                                  {d.storytellerTe}
                                </div>
                                <div className="text-sm text-kk-cream/70 italic truncate">
                                  {storyLine}
                                </div>
                                {d.writerTe && (
                                  <div className="text-xs text-kk-gold/60 truncate">
                                    రచన: {d.writerTe}
                                  </div>
                                )}
                              </div>
                              {d.perf && (
                                <FaPlay className="text-kk-gold/70 flex-shrink-0" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Highlight Reels */}
            {reels.length > 0 && (
              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-semibold text-kk-gold mb-4">
                  హైలైట్ రీల్స్ · Highlight Reels
                </h2>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {reels.map((reel) => {
                    const vid =
                      reel.youtube_video_id || extractYouTubeId(reel.youtube_url);
                    const thumb =
                      reel.thumbnail_url ||
                      (vid
                        ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg`
                        : null);
                    return (
                      <button
                        key={reel.reel_id}
                        type="button"
                        onClick={() => playReel(reel)}
                        className="flex-shrink-0 w-44 md:w-56 group relative"
                      >
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={reel.title || "Highlight"}
                            className="w-full aspect-video object-cover rounded-lg border border-kk-gold/30 group-hover:border-kk-gold"
                          />
                        ) : (
                          <div className="w-full aspect-video bg-kk-maroon/40 rounded-lg border border-kk-gold/30" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                          <FaPlay className="text-kk-gold-light text-2xl drop-shadow" />
                        </div>
                        <div className="text-xs text-kk-cream mt-1.5 truncate">
                          {reel.title || "Highlight"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Nominees Table */}
            <section>
              <h2 className="text-lg md:text-xl font-semibold text-kk-gold mb-4">
                నామినీలు · All Nominees{" "}
                <span className="text-kk-cream/50 text-sm font-normal">
                  ({shortlist.length})
                </span>
              </h2>
              {shortlist.length === 0 ? (
                <p className="text-kk-cream/60">No nominees on file for this category.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm md:text-base border-separate border-spacing-y-1">
                    <thead className="text-kk-gold/80 text-xs md:text-sm uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">కథకుడు · Storyteller</th>
                        <th className="text-left px-3 py-2">కథ · Story</th>
                        <th className="text-left px-3 py-2 hidden md:table-cell">
                          రచన · Writer
                        </th>
                        <th className="text-right px-3 py-2">Play</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shortlist.map((sl) => {
                        const st = storytellersById[sl.storyteller_id];
                        const sto = sl.story_id ? storiesById[sl.story_id] : null;
                        const writer = sto?.writer_id ? writersById[sto.writer_id] : null;
                        const stTe =
                          st?.storyteller_telugu || sl.storyteller_name_telugu || "—";
                        const storyTe = sto?.title_telugu || sl.story_name_telugu || "—";
                        const writerTe = isWriterCat
                          ? "స్వీయ రచన"
                          : writer?.writer_telugu || "—";
                        const ep = sl.episode;
                        const vid = ep?.video_id || ep?.youtube_broadcast_id;
                        const canPlay = !!vid;
                        return (
                          <tr
                            key={sl.performance_id}
                            className="bg-kk-ink/40 hover:bg-kk-ink/70 transition-colors"
                          >
                            <td className="px-3 py-2 rounded-l-lg">
                              <div className="flex items-center gap-2">
                                {st?.photo_url ? (
                                  <img
                                    src={st.photo_url}
                                    alt={stTe}
                                    className="w-7 h-7 rounded-full object-cover border border-kk-gold/40"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <FaUserCircle className="text-kk-gold/40 text-2xl" />
                                )}
                                <span className="text-kk-cream truncate">{stTe}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-kk-cream/85 italic">
                              {storyTe}
                            </td>
                            <td className="px-3 py-2 text-kk-gold/80 hidden md:table-cell">
                              {writerTe}
                            </td>
                            <td className="px-3 py-2 rounded-r-lg text-right">
                              {canPlay ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveVideo({
                                      videoId: vid,
                                      startSec: sl.start_sec || 0,
                                    });
                                    setActiveMeta({
                                      title: `${stTe} — ${storyTe}`,
                                      meta: ep?.title || "",
                                      url: `https://www.youtube.com/watch?v=${vid}${
                                        sl.start_sec ? `&t=${sl.start_sec}` : ""
                                      }`,
                                    });
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                  }}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-kk-gold/15 border border-kk-gold/40 rounded-md text-kk-gold-light hover:bg-kk-gold/25 hover:border-kk-gold text-xs md:text-sm"
                                >
                                  <FaPlay className="text-[10px]" /> Play
                                </button>
                              ) : (
                                <span className="text-kk-cream/30 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <div className="mt-10 pt-6 border-t border-kk-gold/15 text-center">
              <Link
                to={`/jeedigunta/${seasonSlug}`}
                className="inline-flex items-center gap-2 text-kk-gold/80 hover:text-kk-gold text-sm"
              >
                <FaArrowLeft /> Other categories in {seasonSlug}
              </Link>
            </div>
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JeedigntaCategory;
